
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, BookOpen } from 'lucide-react';
import { Dialog } from '@/components/ui/dialog';
import CivicEducationTable from './CivicEducationTable';
import CivicEducationForm from './CivicEducationForm';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCivicEducationManagement } from '@/hooks/admin/civic-education/useCivicEducationManagement';
import RelationshipManager from './RelationshipManager';
import { useCivicEducationRelationships } from '@/hooks/admin/civic-education/useCivicEducationRelationships';
import { useIsMobile } from '@/hooks/use-mobile';
import CivicEducationFilters from './CivicEducationFilters';
import CivicEducationPagination from './CivicEducationPagination';
import { useCivicEducationPagination } from '@/hooks/admin/civic-education/useCivicEducationPagination';

const CivicEducationManagement = () => {
  const {
    items,
    isLoading,
    dialogOpen,
    setDialogOpen,
    currentItem,
    setCurrentItem,
    isEditing,
    contentType,
    setContentType,
    linkedCards,
    setLinkedCards,
    handleOpenDialog,
    handleSaveItem,
    handleDeleteItem
  } = useCivicEducationManagement();

  const [activeTab, setActiveTab] = useState<string>('content');
  const [relationshipDialogOpen, setRelationshipDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  
  const { fetchRelationships, saveRelationships } = useCivicEducationRelationships();
  const isMobile = useIsMobile();

  // Setup pagination, filtering, sorting
  const {
    paginatedItems,
    pagination,
    searchTerm,
    setSearchTerm,
    sortField,
    sortDirection,
    handleSort,
    totalItems
  } = useCivicEducationPagination({ items });

  // Handle opening the relationship dialog
  const handleManageRelationships = async (itemId: string) => {
    const relationships = await fetchRelationships(itemId);
    setLinkedCards(relationships);
    setSelectedItem(itemId);
    setRelationshipDialogOpen(true);
  };

  // Create a new content item with proper type handling
  const handleCreateNew = () => {
    // Pass empty item object to handleOpenDialog instead of no arguments
    handleOpenDialog(undefined);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setContentType('all');
    pagination.setCurrentPage(1);
  };

  return (
    <>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`mb-4 ${isMobile ? 'w-full flex' : ''}`}>
          <TabsTrigger value="content" className={isMobile ? "flex-1" : ""}>Content Management</TabsTrigger>
          <TabsTrigger value="relationships" className={isMobile ? "flex-1" : ""}>Card Relationships</TabsTrigger>
        </TabsList>
        
        <TabsContent value="content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen size={20} />
                Civic Education Content
              </CardTitle>
              <CardDescription>
                Manage educational content linked to insight cards
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`mb-4 ${isMobile ? 'flex flex-col space-y-4' : 'flex justify-between'}`}>
                <CivicEducationFilters
                  contentType={contentType}
                  setContentType={setContentType}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  onClearFilters={handleClearFilters}
                />
                
                <Button onClick={handleCreateNew} className={isMobile ? "w-full" : ""}>
                  <Plus size={16} className="mr-2" />
                  Add New Content
                </Button>
              </div>
              
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <p>Loading content...</p>
                </div>
              ) : (
                <>
                  <CivicEducationTable 
                    items={paginatedItems} 
                    isLoading={isLoading} 
                    onEdit={handleOpenDialog}
                    onDelete={handleDeleteItem}
                    onManageRelationships={handleManageRelationships}
                    contentType={contentType}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  
                  <CivicEducationPagination 
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    itemsPerPage={pagination.itemsPerPage}
                    totalItems={totalItems}
                    startIndex={pagination.startIndex}
                    endIndex={pagination.endIndex}
                    setCurrentPage={pagination.setCurrentPage}
                    setItemsPerPage={pagination.setItemsPerPage}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="relationships">
          <Card>
            <CardHeader>
              <CardTitle>Insight Card Relationships</CardTitle>
              <CardDescription>
                Manage which insight cards are related to educational content
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RelationshipManager linkedCards={linkedCards} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {currentItem && (
          <CivicEducationForm
            item={currentItem}
            setItem={setCurrentItem}
            onCancel={() => setDialogOpen(false)}
            onSave={handleSaveItem}
            isEditing={isEditing}
            contentType={contentType}
          />
        )}
      </Dialog>
      
      <Dialog open={relationshipDialogOpen} onOpenChange={setRelationshipDialogOpen}>
        {selectedItem && (
          <RelationshipManager 
            itemId={selectedItem} 
            onClose={() => setRelationshipDialogOpen(false)}
            linkedCards={linkedCards}
          />
        )}
      </Dialog>
    </>
  );
};

export default CivicEducationManagement;
